/** Native history owns navigation; this mounted visit retains the last reading position per answer selection. */
import { useCallback, useLayoutEffect, useRef } from 'react'
import { useAppBridge } from '@shopify/app-bridge-react'
import {
  handleShopifyAppLinkClick,
  isProgrammaticShopifyNavigation,
  type ShopifyNavigationClick,
} from '../../components/shopifyAppNavigationClick'
import { leaveShopifyBuilder } from '../../resultsNavigation'
import { reportShopifyNavigationFailure } from './useShopifyAppNavigation'
import { shopifyUiTracker, shopifyUiFailure } from '../../shopifyUiTracking'

type Position = { href: string; top: number | null; focus: 'answers' | 'top' | null }
const href = () => `${window.location.pathname}${window.location.search}${window.location.hash}`
function revealAnswers() {
  const target = document.getElementById('result-answer-controls') ?? document.getElementById('result-authored-answers')
  target?.scrollIntoView({ block: 'start', behavior: 'instant' })
}
function readingKey(url = new URL(window.location.href)) {
  return JSON.stringify([
    url.pathname,
    ...['studyId', 'runId', 'questionId', 'answerArmId', 'answerPage', 'nextTestsPage', 'phrasesPage'].map((key) =>
      url.searchParams.get(key),
    ),
  ])
}

export function useResultPageNavigation({
  visit,
  selectionKey,
  ready,
  available,
}: {
  visit: string
  selectionKey: string
  ready: boolean
  available: boolean
}) {
  const shopify = useAppBridge()
  const pending = useRef<Position | null>(null)
  const save = useRef(() => {})
  const active = useRef(true)
  const attempt = useRef(0)
  // App Bridge replaces iframe history entries while the parent owns traversal.
  // Never rely on custom iframe history.state surviving a native Back/Forward.
  const positions = useRef(new Map<string, number>())
  useLayoutEffect(() => {
    active.current = true
    positions.current.clear()
    const entry = new URL(window.location.href)
    const belongs = () => {
      const now = new URL(window.location.href)
      return (
        now.pathname === entry.pathname &&
        now.searchParams.get('studyId') === entry.searchParams.get('studyId') &&
        now.searchParams.get('runId') === entry.searchParams.get('runId')
      )
    }
    const previousRestoration = window.history.scrollRestoration
    window.history.scrollRestoration = 'manual'
    const remember = () => {
      if (pending.current || !belongs()) return
      positions.current.set(readingKey(), window.scrollY)
    }
    save.current = remember
    const onPopState = () => {
      const target = href()
      if (pending.current?.href === target) return
      pending.current = { href: target, top: positions.current.get(readingKey()) ?? null, focus: null }
    }
    window.addEventListener('scroll', remember, { passive: true })
    window.addEventListener('popstate', onPopState)
    return () => {
      active.current = false
      attempt.current++
      remember()
      window.removeEventListener('scroll', remember)
      window.removeEventListener('popstate', onPopState)
      window.history.scrollRestoration = previousRestoration
      pending.current = null
      save.current = () => {}
    }
  }, [visit])

  // Wait for the checked matching selection, never scroll in response to a stale or failed reply.
  useLayoutEffect(() => {
    if (!ready || !available || !pending.current) return
    const position = pending.current
    const frame = window.requestAnimationFrame(() => {
      if (pending.current !== position || position.href !== href()) return
      pending.current = null
      if (position.focus === 'answers') {
        revealAnswers()
        document.getElementById('result-authored-answers')?.focus({ preventScroll: true })
      } else if (position.focus === 'top') window.scrollTo({ top: 0, behavior: 'instant' })
      else if (position.top !== null) window.scrollTo({ top: position.top, behavior: 'instant' })
      save.current()
    })
    return () => window.cancelAnimationFrame(frame)
  }, [ready, available, selectionKey])

  return useCallback(
    (event: ShopifyNavigationClick, target: string, focus: 'answers' | 'top') => {
      const next = new URL(target, window.location.href)
      const programmatic = isProgrammaticShopifyNavigation(event)
      const expectedShop = new URL(window.location.href).searchParams.get('shop')
      const tracker = shopifyUiTracker(shopify)
      // Request inside the accepted navigation callback, not a host Back/Forward replay or modified native link.
      let uiAttempt: ReturnType<typeof tracker.request>
      void handleShopifyAppLinkClick(event, target, expectedShop, async (commit) => {
        if (!programmatic) uiAttempt = tracker.request(focus === 'answers' ? 'select-question' : 'open-editor')
        const intent = ++attempt.current
        const proceed = () => {
          if (!active.current || attempt.current !== intent) return
          save.current()
          let targetTop: number | null = null
          let targetFocus: Position['focus'] = focus
          if (programmatic) {
            targetTop = positions.current.get(readingKey(next)) ?? null
            targetFocus = null
          }
          pending.current = { href: `${next.pathname}${next.search}${next.hash}`, top: targetTop, focus: targetFocus }
          commit()
          if (focus === 'answers' && !programmatic) revealAnswers()
        }
        // Read-only selection stays on this page; only a real departure needs native leave protection.
        if (focus === 'answers') {
          proceed()
          return true
        }
        return leaveShopifyBuilder(shopify, proceed)
      })
        .then((accepted) => {
          if (accepted) tracker.completed(uiAttempt)
          else tracker.cancelled(uiAttempt, 'user')
        })
        .catch((cause) => {
          tracker.failed(uiAttempt, shopifyUiFailure(cause))
          reportShopifyNavigationFailure(shopify, cause)
        })
    },
    [shopify, visit],
  )
}
